import {
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import * as React from "react";
import userService from "../api/services/user-service";
import { showErrorToast, showSuccessToast } from "../helpers/utils";
import unauth from "../images/unauth.png";
import UserModel from "../models/user-model";
import { PermissionsContext } from "../permission-context";

const UserManagement = () => {
  const { loading, error, hasPermission } = React.useContext(PermissionsContext);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [users, setUsers] = React.useState([]); // Placeholder user data
  const [userDetails, setUserDetails] = React.useState({
    name: "",
    email: "",
    isActive: false,
    permissions: [],
  });

  // Define valid permissions for accurate counting
  const validPermissions = [
    "Admin",
    "EMR",
    "PatientVisits",
    "MER",
    "ViewVScribeAiPhoneDictStatus",
    "AppDeviceInfo",
    "AppNotificationLogs",
  ];

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    userService
      .getUsers()
      .then((response) => {
        const sortedUsers = response.data.sort((a, b) => b.id - a.id); // Sort by ID in descending order
        setUsers(Array.from(sortedUsers));
      })
      .catch((error) => {
        // Handle error as needed (show message to user, retry, etc.)
      });
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    // Placeholder user details
    setUserDetails({
      name: user.name,
      email: user.email,
      isActive: user.isActive,
      permissions: user.roles || [], // Ensure roles array exists and is empty if null
    });
  };

  const handleInputChange = (event) => {
    const { name, value, checked, type } = event.target;

    if (type === "checkbox") {
      setUserDetails({ ...userDetails, [name]: checked });
    } else if (type === "select-multiple") {
      const selectedOptions = Array.from(event.target.selectedOptions, (option) => option.value);
      setUserDetails({ ...userDetails, [name]: selectedOptions });
    } else {
      setUserDetails({ ...userDetails, [name]: value });
    }
  };

  async function handleSave() {
    // Create UserModel instance based on userDetailsListItem
    const userModel = new UserModel(
      selectedUser.id,
      userDetails.name,
      userDetails.email,
      userDetails.permissions,
      userDetails.isActive
    );

    try {
      await userService.updateUser(userModel);
      showSuccessToast("Updated successfully");
      const updatedUsers = users.map((user) => (user.id === selectedUser.id ? userModel : user));
      setUsers(updatedUsers);
    } catch (error) {
      showErrorToast("Update Failed");
    }
  }

  const handleCancel = () => {
    // Handle cancel logic, reset userDetails to initial state
    setSelectedUser(null);
    setUserDetails({
      name: "",
      email: "",
      isActive: false,
      permissions: [],
    });
  };

  return (
    <>
      {!loading && !error && hasPermission("Admin") && (
        <Box sx={{ p: 2 }}>
          <Typography gutterBottom fontWeight='700' lineHeight={"31.25px"} fontSize={"24px"}>
            User Management
          </Typography>
          <Box sx={{ display: "flex" }}>
            <Paper sx={{ width: "30%", height: "80vh", overflow: "auto", mr: 2 }}>
              <List>
                {users.map((user, index) => {
                  // Filter roles to count only valid permissions
                  const permissionCount = user.roles
                    ? user.roles.filter((role) => validPermissions.includes(role)).length
                    : 0;

                  return (
                    <ListItem key={index} disablePadding>
                      <ListItemButton
                        onClick={() => handleUserClick(user)}
                        selected={selectedUser === user}>
                        <Box
                          display='flex'
                          justifyContent='space-between'
                          width='100%'
                          alignItems='center'>
                          <Box display='flex' alignItems='center'>
                            <span style={{ marginRight: "8px" }}>{user.name}</span>
                            {!user.isActive && (
                              <Chip
                                variant='outlined'
                                label='Disabled'
                                style={{
                                  backgroundColor: "rgba(119, 119, 119, 0.2)",
                                  color: "#777777",
                                  marginRight: "4px",
                                }}
                                size='medium'
                              />
                            )}
                          </Box>

                          <Box display='flex' alignItems='center'>
                            {user.roles && user.roles.includes("Admin") && (
                              <Chip
                                variant='outlined'
                                label='Admin'
                                style={{
                                  backgroundColor: "rgba(0, 190, 30, 0.2)",
                                  color: "#00BE1E",
                                  marginRight: "4px",
                                }}
                                size='medium'
                              />
                            )}
                            <Box
                              sx={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                backgroundColor: "#F0F0F0",
                                color: "#333333",
                                border: "1px solid #D9D9D9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                fontWeight: "700",
                                marginLeft: "8px",
                              }}>
                              {permissionCount}
                            </Box>
                          </Box>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>

            {selectedUser && (
              <Paper sx={{ width: "70%", height: "80vh", overflow: "auto", p: 2 }}>
                <TextField
                  label='Name'
                  value={userDetails.name}
                  name='name'
                  id='name'
                  autoComplete='off'
                  InputProps={{ readOnly: true }}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label='Email'
                  value={userDetails.email}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={userDetails.isActive}
                      onChange={(event) =>
                        setUserDetails({
                          ...userDetails,
                          isActive: event.target.checked,
                        })
                      }
                      name='isLoginEnabled'
                      autoComplete='off'
                      id='isLoginEnabled'
                    />
                  }
                  label='Is Login Enabled'
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id='permissions-label'>Permissions</InputLabel>
                  <Select
                    labelId='permissions-label'
                    id='permissions'
                    multiple
                    value={userDetails.permissions}
                    label='Permissions'
                    name='permissions'
                    onChange={handleInputChange}>
                    {validPermissions.map((perm) => (
                      <MenuItem key={perm} value={perm}>
                        {perm === "ViewVScribeAiPhoneDictStatus"
                          ? "VScribeAIDictationStatus"
                          : perm}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button variant='contained' color='primary' sx={{ mr: 1 }} onClick={handleSave}>
                    Save
                  </Button>
                  <Button variant='outlined' onClick={handleCancel}>
                    Cancel
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      )}
      {!loading && !error && !hasPermission("Admin") && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "90vh",
          }}>
          <img src={unauth} alt='UnAuthorized' style={{ maxWidth: "100%", maxHeight: "100%" }} />
        </Box>
      )}
    </>
  );
};

export default UserManagement;
